import * as Sentry from "@sentry/react";
import { getAuthToken } from "@/contexts/AuthContext";
import { isTauri } from "@/hooks/usePlatform";

// Direct API URL (used in Tauri production + web production)
const API_GATEWAY_BASE = "https://api.colony.so";

// Cache the Tauri HTTP fetch function (lazy-loaded to avoid import errors in web)
let tauriFetch: typeof globalThis.fetch | null = null;

/**
 * In Tauri production mode, use the Tauri HTTP plugin's fetch().
 * It runs through Rust and bypasses CORS restrictions entirely.
 * In web/dev mode, use the standard browser fetch (proxied by Express).
 */
async function getTauriFetch(): Promise<typeof globalThis.fetch> {
  if (!tauriFetch) {
    const mod = await import("@tauri-apps/plugin-http");
    tauriFetch = mod.fetch;
  }
  return tauriFetch;
}

function useDirectApi(): boolean {
  return isTauri() && import.meta.env.PROD;
}

function getBaseUrl(): string {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  if (useDirectApi()) {
    return API_GATEWAY_BASE;
  }
  // Web production (Amplify): call API Gateway directly (no Express proxy)
  if (import.meta.env.PROD && !isTauri()) {
    return API_GATEWAY_BASE;
  }
  return ""; // dev: use Express proxy
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getAuthToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`${response.status}: ${text || response.statusText}`);
  }
  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text);
}

async function apiFetch<T>(endpoint: string, options: RequestInit): Promise<T> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${endpoint}`;

  // In Tauri production: use Tauri HTTP plugin (bypasses CORS)
  // In web/dev: use browser fetch (Express proxy handles /api/*)
  const fetchFn = useDirectApi() ? await getTauriFetch() : globalThis.fetch;

  Sentry.addBreadcrumb({
    category: "api",
    message: `${options.method} ${endpoint}`,
    level: "info",
  });

  try {
    const response = await fetchFn(url, options);
    return handleResponse<T>(response);
  } catch (error) {
    Sentry.captureException(error, {
      tags: { endpoint, method: options.method },
    });
    throw error;
  }
}

export const api = {
  async get<T>(endpoint: string): Promise<T> {
    return apiFetch<T>(endpoint, {
      method: "GET",
      headers: await getAuthHeaders(),
    });
  },

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return apiFetch<T>(endpoint, {
      method: "POST",
      headers: await getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return apiFetch<T>(endpoint, {
      method: "PATCH",
      headers: await getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return apiFetch<T>(endpoint, {
      method: "PUT",
      headers: await getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  async delete<T>(endpoint: string): Promise<T> {
    return apiFetch<T>(endpoint, {
      method: "DELETE",
      headers: await getAuthHeaders(),
    });
  },
};
